import {
  GatewayEntreprise,
  GatewayFormationContinue,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import {
  IconStatus,
  InfoCard,
  isIas,
  isIobsp,
  InputButtonGroup,
  InputNumber,
  InputText,
  useFormConfig,
  Button,
  FormField,
  FormGrid,
  FormRow,
  FormTitle,
} from "@endya-conformite/ui-kit";
import PlusCircle from "@endya-conformite/ui-kit/src/assets/icons/plus-circle.svg?react";
import TrashIcon from "@endya-conformite/ui-kit/src/assets/icons/trash.svg?react";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  useFieldArray,
} from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import { GatewayFormationContinueApi } from "@src/api/conformite/formationContinue.api";
import {
  formatDate,
  isBeforeToday,
  wasLessThanOneYearAgo,
} from "@src/helper/date.helper";
import { useAssujetti } from "@src/store/store.assujetti";
import { isToday } from "date-fns";
import { isNil } from "lodash";
import { AssujettiConformiteData } from "../../../AssujettiParcoursConformite";
import { NextVerificatioCard } from "../../../Components/NextVerificationCard/NextVerificationCard";
import styles from "./ConformiteFormFormationIAS.module.scss";

export const formationContinueIasSchema = zod.object({
  id: zod.string().optional(),
  dateDernierVerification: zod
    .date()
    .refine((value) => {
      if (!value) return false;
      return wasLessThanOneYearAgo(value);
    }, "La dernière vérification date d'il y a plus d'un an. Elle n'est donc pas valable. Merci de vérifier à nouveau")
    .refine((value) => {
      if (!value) return false;
      return isBeforeToday(value) || isToday(value);
    }, "La date doit être inférieure ou égale à la date du jour."),
  soumisFormationContinue: zod.boolean(),
  formations: zod.array(
    zod.object({
      theme: zod.string(),
      nombreHeures: zod.number({
        required_error: "Champs requis",
      }),
    })
  ),
});
const getDefaultValueSoumisFormationContinue = (
  formData: GatewayFormationContinue.FormationContinueIas | null,
  isOnlyIAS: boolean
) => {
  if (isOnlyIAS) return true;
  if (!isNil(formData?.soumisFormationContinue))
    return formData?.soumisFormationContinue;
  return null;
};
export type FormationContinueIasSchemaFormValues = zod.infer<
  typeof formationContinueIasSchema
>;
export const useFormationContinueIasForm = (
  formData: GatewayFormationContinue.FormationContinueIas | null,
  categories: GatewayInfoSocieteOrias.Categorie[],
  categoriesAccessoires: GatewayInfoSocieteOrias.Categorie[]
) => {
  const categoriesWithAccessoires = [...categories, ...categoriesAccessoires];

  const isOnlyIAS =
    isIas(categoriesWithAccessoires) &&
    !isIobsp(categoriesWithAccessoires) &&
    categories.length > 0; // A au moins 1 categorie non accessoire;

  const formConfig = useFormConfig<
    typeof formationContinueIasSchema,
    FormationContinueIasSchemaFormValues
  >({
    schema: formationContinueIasSchema,
    defaultValues: {
      id: formData?.id,
      dateDernierVerification: formData?.dateDernierVerification ?? new Date(),
      soumisFormationContinue: getDefaultValueSoumisFormationContinue(
        formData,
        isOnlyIAS
      ),
      formations:
        formData?.formations && formData?.formations?.length > 0
          ? formData?.formations
          : [{ theme: null, nombreHeures: null }],
    } as FormationContinueIasSchemaFormValues,
  });

  const { fields, append, remove } =
    useFieldArray<FormationContinueIasSchemaFormValues>({
      control: formConfig.control,
      name: "formations",
    });

  const [lastVerifiedAt, soumisFormationContinue, formations] =
    formConfig.watch([
      "dateDernierVerification",
      "soumisFormationContinue",
      "formations",
    ]);

  const totalHours = formations.reduce(
    (total, formation) => total + (Number(formation.nombreHeures) || 0),
    0
  );

  return {
    formConfig,
    fields,
    append,
    remove,
    totalHours,
    lastVerifiedAt,
    soumisFormationContinue,
    isOnlyIAS,
  };
};
export const useFormationContinueIas = (
  formData: GatewayFormationContinue.FormationContinueIas | null,
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType | undefined,
  data: AssujettiConformiteData,
  setFormData: (data: GatewayFormationContinue.FormationContinueIas) => void
) => {
  const {
    formConfig,
    fields,
    append,
    remove,
    totalHours,
    lastVerifiedAt,
    soumisFormationContinue,
    isOnlyIAS,
  } = useFormationContinueIasForm(
    formData,
    entreprise?.categoriesActivites ?? [],
    entreprise?.categoriesActivitesAccessoires ?? []
  );

  const { updateAvancement } = useAssujetti();
  const onSubmit = async (values: FormationContinueIasSchemaFormValues) => {
    const valuesToSubmit: GatewayFormationContinue.FormationContinueIas = {
      dateDernierVerification: values.dateDernierVerification ?? null,
      soumisFormationContinue: values.soumisFormationContinue ?? null,
      formations: values.formations.map((formation) => ({
        nombreHeures: formation.nombreHeures ?? null,
        theme: formation.theme ?? null,
      })),
      id: values.id,
    };
    const avancement = await GatewayFormationContinueApi.saveIAS(
      data.assujetti.id,
      data.assujetti.millesime,
      valuesToSubmit
    );
    updateAvancement(data.assujetti.millesime, data.assujetti.id, avancement);
    setFormData(valuesToSubmit);
  };

  return {
    formConfig,
    fields,
    append,
    remove,
    onSubmit,
    totalHours,
    lastVerifiedAt,
    soumisFormationContinue,
    isOnlyIAS,
  };
};

type ConformiteFormFormationIasContent = {
  hideTitle?: boolean;
  data: AssujettiConformiteData;
  totalHours: number;
  lastVerifiedAt: Date;
  fields: FieldArrayWithId<
    FormationContinueIasSchemaFormValues,
    "formations"
  >[];
  append: UseFieldArrayAppend<
    FormationContinueIasSchemaFormValues,
    "formations"
  >;
  remove: UseFieldArrayRemove;
  soumisFormationContinue: boolean;
  isOnlyIAS: boolean;
};
export function ConformiteFormFormationIasContent({
  hideTitle,
  data,
  totalHours,
  append,
  remove,
  lastVerifiedAt,
  fields,
  soumisFormationContinue,
  isOnlyIAS,
}: ConformiteFormFormationIasContent) {
  const name = ` - ${data.assujetti.surname} ${data.assujetti.name}`;
  const { millesime } = data.assujetti;

  return (
    <FormGrid disableHorizontalGutter>
      {!hideTitle && (
        <FormTitle className={styles.header}>
          <h2 className={styles.title}>
            <b>Formation continue IAS</b>
            {name}
          </h2>
          <IconStatus
            isValid={data.assujetti.avancement.formationContinueIas === 100}
          />
        </FormTitle>
      )}
      {!isOnlyIAS && (
        <FormRow>
          <FormField>
            <InputButtonGroup<
              FormationContinueIasSchemaFormValues,
              false,
              boolean
            >
              name="soumisFormationContinue"
              label="Est-elle soumise à la formation continue ?"
              options={[
                {
                  label: "Oui",
                  value: true,
                },
                {
                  label: "Non",
                  value: false,
                },
              ]}
            />
          </FormField>
        </FormRow>
      )}
      {soumisFormationContinue && (
        <>
          <FormRow>
            <FormField>
              <div className={styles.dateSaisie}>
                Date de saisie : {formatDate(lastVerifiedAt)}
              </div>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <h1>{millesime - 1}</h1>
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <InfoCard type="info" className={hideTitle ? styles.notice : ""}>
                Afin de répondre aux obligations légales, la personne doit avoir
                suivi une formation de <strong>15h minimum</strong> en{" "}
                {millesime - 1}
              </InfoCard>
            </FormField>
          </FormRow>
          {fields.map(({ id }, index) => (
            <>
              <FormRow>
                <FormField className={styles.formationTitle}>
                  Formation {index + 1}
                </FormField>
              </FormRow>
              <FormRow key={id} className={styles.formationRow}>
                <FormField size="half" takeFullSizeMobile={false}>
                  <InputText name={`formations.${index}.theme`} label="Thème" />
                </FormField>
                <FormField
                  size="half"
                  takeFullSizeMobile={false}
                  className={styles.hoursField}
                >
                  <InputNumber
                    name={`formations.${index}.nombreHeures`}
                    label="Nombre d'heures"
                  />
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => remove(index)}
                      className={styles.trashButton}
                      variant="text"
                      iconVariant="left"
                      icon={<TrashIcon />}
                    />
                  )}
                </FormField>
              </FormRow>
            </>
          ))}
          <FormRow>
            <FormField>
              <Button
                variant="text"
                icon={<PlusCircle />}
                iconVariant="left"
                onClick={() =>
                  append({
                    nombreHeures: null,
                    theme: null,
                  } as unknown as FormationContinueIasSchemaFormValues["formations"][0])
                }
              >
                Ajouter une formation supplémentaire
              </Button>
            </FormField>
          </FormRow>

          {totalHours < 15 && (
            <>
              <FormRow>
                <FormField>
                  Total des heures renseignées : <strong>{totalHours}h</strong>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InfoCard type="warning">
                    Le total des heures de formation est insuffisant.
                  </InfoCard>
                </FormField>
              </FormRow>
            </>
          )}
        </>
      )}
      <NextVerificatioCard lastVerifiedAt={lastVerifiedAt} />{" "}
    </FormGrid>
  );
}
